import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { Mail, Lock, User, Eye, EyeOff, Loader2 } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';
import apiClient from '../services/apiClient';
import { getStoredReferral } from '../utils/couponReferral';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState(null);

  // Arrived through a consultant's ?ref= link: show the discount up front.
  useEffect(() => {
    const code = getStoredReferral();
    if (!code) return;
    apiClient.get(`/api/affiliates/validate/${encodeURIComponent(code)}`)
      .then((res) => { if (res.data.valid) setReferralDiscount(res.data); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    if (name.trim().length < 2) {
      toast.error('El nombre debe tener al menos 2 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    
    const result = await register(email, password, name.trim());
    
    setLoading(false);
    
    if (result.success) {
      toast.success('Cuenta creada exitosamente');
      // An admin-invited consultant lands in their affiliate area instead.
      navigate(result.user.role === 'consultant' ? '/afiliados' : '/select-plan');
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <BrandLogo className="h-8 mx-auto mb-3" />
          <p className="text-muted-foreground">Crea tu cuenta</p>
        </div>

        {referralDiscount && (
          <div className="mb-4 rounded-lg border border-green-500/40 bg-green-500/10 px-4 py-3 text-center text-sm text-foreground">
            {referralDiscount.program === 'friend' ? (
              <>
                {referralDiscount.referrer_name ? <strong>{referralDiscount.referrer_name}</strong> : 'Un amigo'} te invitó a Selltrix: obtienes un{' '}
                <strong>{referralDiscount.percent_off}% de descuento</strong> en tu primer mes.
              </>
            ) : (
              <>
                Te registras con el enlace de un consultor Selltrix: obtienes un{' '}
                <strong>{referralDiscount.percent_off}% de descuento</strong> durante {referralDiscount.months} meses.
              </>
            )}
          </div>
        )}

        {/* Register Form */}
        <div className="bg-card/60 backdrop-blur-xl border border-border rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name - OBLIGATORIO */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Nombre completo <span className="text-red-500 dark:text-red-400">*</span></label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre completo"
                  className="pl-10 bg-muted border-border text-foreground placeholder:text-muted-foreground/70"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Correo electrónico</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@correo.com"
                  className="pl-10 bg-muted border-border text-foreground placeholder:text-muted-foreground/70"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  className="pl-10 pr-10 bg-muted border-border text-foreground placeholder:text-muted-foreground/70"
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground/70 hover:text-muted-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">Confirmar contraseña</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground/70" />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repite tu contraseña"
                  className="pl-10 bg-muted border-border text-foreground placeholder:text-muted-foreground/70"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-500 text-white h-11"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creando cuenta...
                </>
              ) : (
                'Crear cuenta'
              )}
            </Button>
          </form>

          {/* Login Link */}
          <p className="text-center text-muted-foreground text-sm mt-6">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
              Ingresar
            </Link>
          </p>
        </div>

        {/* Info */}
        <p className="text-center mt-6 text-muted-foreground/70 text-xs">
          El registro está limitado a usuarios autorizados.
        </p>

        {/* Back to Landing */}
        <p className="text-center mt-6">
          <Link to="/" className="text-muted-foreground/70 hover:text-muted-foreground text-sm">
            ← Volver al inicio
          </Link>
        </p>
      </div>
    </div>
  );
};

export default Register;
